// Phantom slip panel. Reused by /brief and the legacy /dashboard.
// 9C.10b: cards spread cardLow + transitionStandard via sibling
// rails; each eyebrow gains a registered icon; MetaTags pills use
// shared pillAmberOutline / pillNeutral; FixtureRow leads with a
// domain.football icon; currency uses formatVaultCedis. Behavior
// preserved verbatim — usePhantomCycle / createCycle / confirmCycle /
// resolveCycle, busy semantics, every render branch, FixtureRow
// fallbacks, SafetyDisclaimer.

import type { CSSProperties } from "react";

import { Icon } from "../../components/icons/Icon.tsx";
import type { IconKey } from "../../components/icons/icon-types.ts";
import { formatVaultCedis } from "../../modes/vault/vault-currency.ts";
import { usePhantomCycle, type PhantomSelection } from "../../phantom/usePhantomCycle.ts";
import { pillAmberOutline, pillNeutral } from "../../styles/status-pills.ts";
import * as d from "./dashboard.styles.ts";
import * as p from "./phantom-slip-panel.styles.ts";
import * as r from "./phantom-slip-panel-rails.styles.ts";
import { PhantomTerminalNote, type PhantomResultPayload } from "./PhantomTerminalNote.tsx";

interface PhantomSlipPanelProps {
  optedOut: boolean;
}

const cardStyle: CSSProperties = { ...d.card, ...r.cardRails };
const wrap: CSSProperties = {
  ...cardStyle,
  borderTop: "2px solid var(--sz-amber)",
};

function CardEyebrow({ icon, label }: { icon: IconKey; label: string }): JSX.Element {
  return (
    <span style={r.headerLead}>
      <Icon name={icon} size="sm" decorative />
      <span style={d.cardEyebrow}>{label}</span>
    </span>
  );
}

function MetaTags({ status }: { status: string }): JSX.Element {
  return (
    <div style={p.metaRow}>
      <span style={pillAmberOutline}>Phantom simulation</span>
      <span style={pillNeutral}>source · manual_seed</span>
      <span style={pillNeutral}>mode · phantom</span>
      <span style={pillNeutral}>status · {status}</span>
    </div>
  );
}

function SafetyDisclaimer(): JSX.Element {
  return (
    <div style={p.disclaimer}>
      <span>This is simulated scaffolding, not a live betting recommendation.</span>
      <span>No real odds are attached.</span>
      <span>No bet has been placed and no real money is involved.</span>
    </div>
  );
}

function FixtureRow({ fixture, index }: { fixture: PhantomSelection; index: number }): JSX.Element {
  const matchup =
    fixture.home_team !== undefined &&
    fixture.home_team !== null &&
    fixture.away_team !== undefined &&
    fixture.away_team !== null
      ? `${fixture.home_team} vs ${fixture.away_team}`
      : `Fixture ${String(index + 1)}`;
  const kickoff =
    typeof fixture.kickoff_at === "string" && fixture.kickoff_at.length > 0
      ? new Date(fixture.kickoff_at).toUTCString()
      : "TBC";
  return (
    <div style={p.fixtureCard}>
      <div style={p.fixtureHeader}>
        <span style={r.fixtureLead}>
          <Icon name="domain.football" size="sm" decorative />
          <span style={p.fixtureTeams}>{matchup}</span>
        </span>
        <span style={p.fixtureLeague}>
          {fixture.league_name ?? "–"}
          {fixture.country_code ? ` · ${fixture.country_code}` : ""}
        </span>
      </div>
      <div style={p.fixtureMeta}>
        <span style={p.fixtureMetaItem}>
          <span style={p.fixtureMetaKey}>kickoff</span>
          <span>{kickoff}</span>
        </span>
        <span style={p.fixtureMetaItem}>
          <span style={p.fixtureMetaKey}>phantom pick</span>
          <span>{fixture.phantom_pick ?? "–"}</span>
        </span>
        <span style={p.fixtureMetaItem}>
          <span style={p.fixtureMetaKey}>source</span>
          <span>{fixture.source ?? "–"}</span>
        </span>
      </div>
    </div>
  );
}

function OptedOutCard(): JSX.Element {
  return (
    <section style={cardStyle} aria-label="Phantom slip">
      <CardEyebrow icon="lifecycle.rejected" label="Phantom slip" />
      <h2 style={d.cardTitle}>Phantom Bet was skipped during onboarding</h2>
      <p style={d.cardBodySm}>
        STRIKZA recorded your opt-out acknowledgement. Phantom simulation slips are not generated
        for skipped users in Phase 1A. Real-stake recommendations remain disabled regardless of this
        choice.
      </p>
    </section>
  );
}

function EmptyCycleCard({
  busy,
  error,
  onCreate,
}: {
  busy: boolean;
  error: string | null;
  onCreate: () => void;
}): JSX.Element {
  return (
    <section style={wrap} aria-label="Phantom slip">
      <CardEyebrow icon="mode.daily_intelligence" label="Phantom slip" />
      <h2 style={d.cardTitle}>Create your first Phantom slip</h2>
      <p style={d.cardBodySm}>
        STRIKZA will assemble a simulated slip from manual-seed fixtures. No real money is involved,
        no live provider odds are used, and no agent reasoning is active yet — this is scaffolding
        so later modules have something to read from.
      </p>
      {error ? <div style={p.errorBar}>{error}</div> : null}
      <button type="button" style={p.primaryBtn} disabled={busy} onClick={onCreate}>
        {busy ? "..." : "Create Phantom slip"}
      </button>
    </section>
  );
}

function activeIcon(status: string): IconKey {
  if (status === "phantom_won") return "lifecycle.complete";
  if (status === "phantom_busted") return "process.process_warning";
  return "mode.daily_intelligence";
}

export function PhantomSlipPanel({ optedOut }: PhantomSlipPanelProps): JSX.Element {
  const { cycle, loading, error, busy, createCycle, confirmCycle, resolveCycle } =
    usePhantomCycle();

  if (optedOut) return <OptedOutCard />;

  if (loading) {
    return (
      <section style={cardStyle} aria-label="Phantom slip">
        <CardEyebrow icon="mode.daily_intelligence" label="Phantom slip" />
        <h2 style={d.cardTitle}>Loading your Phantom slip…</h2>
        <p style={d.cardBodySm}>Reading slip_cycles for this user.</p>
      </section>
    );
  }

  if (cycle === null) {
    return (
      <EmptyCycleCard
        busy={busy}
        error={error}
        onCreate={() => {
          void createCycle();
        }}
      />
    );
  }

  const selections = (
    Array.isArray(cycle.selections) ? cycle.selections : []
  ) as PhantomSelection[];
  const isConfirmed = cycle.status === "phantom_confirmed";
  const isWon = cycle.status === "phantom_won";
  const isBusted = cycle.status === "phantom_busted";
  const isTerminal = isWon || isBusted;
  const cycleId = cycle.id;
  const resultPayload = (cycle.result ?? {}) as PhantomResultPayload;

  let title = "Your current Phantom simulation";
  if (isConfirmed) title = "Phantom slip confirmed";
  if (isWon) title = "Simulated result: Won";
  if (isBusted) title = "Simulated result: Busted";

  return (
    <section style={wrap} aria-label="Phantom slip">
      <CardEyebrow icon={activeIcon(cycle.status)} label="Phantom slip" />
      <h2 style={d.cardTitle}>{title}</h2>
      <MetaTags status={cycle.status} />
      <div style={p.summaryRow}>
        <span style={p.summaryLabel}>Simulated stake</span>
        <span style={p.summaryValue}>{formatVaultCedis(cycle.stake_amount)} · simulated</span>
      </div>
      <div style={p.summaryRow}>
        <span style={p.summaryLabel}>Selections</span>
        <span style={p.summaryValue}>{String(selections.length)} fixtures</span>
      </div>
      <div style={p.fixtureList}>
        {selections.map((fixture, idx) => (
          <FixtureRow key={fixture.fixture_id ?? String(idx)} fixture={fixture} index={idx} />
        ))}
      </div>
      {error ? <div style={p.errorBar}>{error}</div> : null}
      {isTerminal ? (
        <PhantomTerminalNote
          status={cycle.status as "phantom_won" | "phantom_busted"}
          selections={selections}
          result={resultPayload}
        />
      ) : isConfirmed ? (
        <button
          type="button"
          style={p.primaryBtn}
          disabled={busy}
          onClick={() => {
            void resolveCycle(cycleId);
          }}
        >
          {busy ? "..." : "Resolve simulated result"}
        </button>
      ) : (
        <button
          type="button"
          style={p.primaryBtn}
          disabled={busy}
          onClick={() => {
            void confirmCycle(cycleId);
          }}
        >
          {busy ? "..." : "Confirm Phantom slip"}
        </button>
      )}
      <SafetyDisclaimer />
    </section>
  );
}
